/**
 * Registry of sandboxed projects on softmaze.at.
 *
 * Each project's apps read a session record from localStorage that this
 * site writes (see `sandbox.ts`); the record's key and the data-key prefix
 * are per project so two sandboxes never share state. Roles are either
 * web apps served under `/demo/<project>/<app>/` (static sandbox builds) or
 * native apps shown as tappable snapshots of the real demo build in a
 * device frame (iOS simulators cannot run outside Apple hardware).
 */

export type RoleKind = 'web' | 'ios' | 'android'

export interface SandboxRoleDef {
  key: string
  /** which app this role opens: a web app id, or a native app id */
  app: string
  /** landing path relative to the app base (web only) */
  home: string
  kind: RoleKind
}

/** One captured screen of a native app and where taps lead. */
export interface SnapshotHotspot {
  /** percentages of the screen's width/height */
  x: number
  y: number
  w: number
  h: number
  to: string
  label: string
}
export interface SnapshotScreen {
  image: string
  hotspots: SnapshotHotspot[]
}
export interface NativeAppDef {
  kind: 'ios' | 'android'
  /** Screens captured from the real demo build; `start` is the first one. Missing = not captured yet. */
  snapshots?: { start: string; screens: Record<string, SnapshotScreen> }
}

export interface SandboxProject {
  id: string
  storagePrefix: string
  sessionKey: string
  startPath: string
  defaultRole: string
  roles: SandboxRoleDef[]
  shop: { name: string; city: string }
  /** native apps by app id (referenced from roles with kind ios/android) */
  native?: Record<string, NativeAppDef>
  /** Preview widths offered for web roles; all three when omitted. */
  devices?: SandboxDevice[]
}

export type SandboxDevice = 'desktop' | 'tablet' | 'phone'

function define(p: Omit<SandboxProject, 'storagePrefix' | 'sessionKey' | 'startPath'>): SandboxProject {
  return {
    ...p,
    storagePrefix: `${p.id}.sandbox.`,
    sessionKey: `${p.id}.sandbox.session`,
    startPath: `/demo/${p.id}`,
  }
}

/** The four-tab bar every PJ driver screen shares (percent of the screen). */
const PJ_TABS: SnapshotHotspot[] = [
  { x: 4, y: 90.5, w: 26, h: 7.5, to: 'home', label: 'Home' },
  { x: 30, y: 90.5, w: 20, h: 7.5, to: 'invoices', label: 'Invoices' },
  { x: 50, y: 90.5, w: 22, h: 7.5, to: 'support', label: 'Support' },
  { x: 72, y: 90.5, w: 24, h: 7.5, to: 'settings', label: 'Settings' },
]
const pjScreen = (image: string, extra: SnapshotHotspot[] = [], tabs = true): SnapshotScreen => ({
  image: `/demo/pjlogistik/snapshots/${image}`,
  hotspots: tabs ? [...extra, ...PJ_TABS] : extra,
})
const PJ_IOS_SNAPSHOTS: NativeAppDef['snapshots'] = {
  start: 'home',
  screens: {
    home: pjScreen('ios/home.jpg', [{ x: 4, y: 33, w: 92, h: 6.5, to: 'invoices', label: 'Pending invoices' }]),
    invoices: pjScreen('ios/invoices.jpg'),
    support: pjScreen('ios/support.jpg', [
      { x: 4, y: 17, w: 92, h: 7.5, to: 'ticket', label: 'Invoice issue ticket' },
      { x: 4, y: 82, w: 92, h: 7, to: 'chatbot', label: 'Chat with support' },
    ]),
    ticket: pjScreen('ios/ticket.jpg', [{ x: 0, y: 6.5, w: 30, h: 6, to: 'support', label: 'Back' }], false),
    chatbot: pjScreen('ios/chatbot.jpg', [{ x: 0, y: 6.5, w: 30, h: 6, to: 'support', label: 'Back' }], false),
    settings: pjScreen('ios/settings.jpg', [{ x: 84, y: 7, w: 12, h: 5.5, to: 'profile', label: 'Profile' }]),
    profile: pjScreen('ios/profile.jpg', [{ x: 0, y: 6.5, w: 30, h: 6, to: 'settings', label: 'Back' }], false),
  },
}

export const PROJECTS: Record<string, SandboxProject> = {
  vollgas: define({
    id: 'vollgas',
    defaultRole: 'MANAGER',
    shop: { name: 'Vollgas KFZ', city: 'Wien' },
    // The Vollgas apps are not laid out for phone widths.
    devices: ['desktop', 'tablet'],
    roles: [
      { key: 'CUSTOMER', app: 'web', home: 'dashboard', kind: 'web' },
      { key: 'MANAGER', app: 'management', home: '', kind: 'web' },
      { key: 'TECHNICIAN', app: 'technical', home: '', kind: 'web' },
      { key: 'ADMIN', app: 'admin', home: '', kind: 'web' },
    ],
  }),
  pjlogistik: define({
    id: 'pjlogistik',
    defaultRole: 'OFFICE_MANAGER',
    shop: { name: 'PJ Logistik', city: 'Wien' },
    roles: [
      { key: 'Admin', app: 'dashboard', home: '', kind: 'web' },
      { key: 'OFFICE_MANAGER', app: 'dashboard', home: '', kind: 'web' },
      { key: 'OFFICE', app: 'dashboard', home: '', kind: 'web' },
      { key: 'DRIVER', app: 'dashboard', home: '', kind: 'web' },
      { key: 'DRIVER_IOS', app: 'ios', home: '', kind: 'ios' },
      { key: 'DRIVER_ANDROID', app: 'android', home: '', kind: 'android' },
    ],
    native: {
      ios: { kind: 'ios', snapshots: PJ_IOS_SNAPSHOTS },
      android: { kind: 'android' },
    },
  }),
  gladiator: define({
    id: 'gladiator',
    defaultRole: 'ADMIN',
    shop: { name: 'Gladiator Logistik', city: 'Wien' },
    // The product has seven roles (Manager, HR, Dispatcher, Fleet manager,
    // Driver…); the demo exposes the two ends of the spectrum.
    roles: [
      { key: 'ADMIN', app: 'dashboard', home: '', kind: 'web' },
      { key: 'EMPLOYEE', app: 'dashboard', home: '', kind: 'web' },
    ],
  }),
}

export function getProject(id: string | undefined): SandboxProject | null {
  return id && Object.prototype.hasOwnProperty.call(PROJECTS, id) ? PROJECTS[id] : null
}

export function roleDef(p: SandboxProject, role: string): SandboxRoleDef {
  return p.roles.find((r) => r.key === role) ?? p.roles[0]
}

export function appBasePath(p: SandboxProject, app: string): string {
  return `${p.startPath}/${app}/`
}

/** Which role owns a given in-iframe pathname, if it is one of this project's web apps. */
export function roleForPath(p: SandboxProject, pathname: string): string | null {
  const m = pathname.match(new RegExp(`^${p.startPath}/([a-z0-9-]+)(/|$)`))
  if (!m) return null
  return p.roles.find((r) => r.kind === 'web' && r.app === m[1])?.key ?? null
}
